import {
  DryRunRecorder,
  createDryRunFileSystem,
  describesChange,
  formatDryRunOperations
} from "../utils/dry-run.js";
import type { FileSystem } from "../utils/file-system.js";
import type {
  CommandRunner,
  CommandRunnerResult
} from "../utils/command-checks.js";
import type { ScopedLogger } from "./logger.js";
import { FEEDBACK_URL } from "./constants.js";

export interface CommandContextOptions {
  dryRun: boolean;
  logger: ScopedLogger;
  runner: CommandRunner;
}

export interface CommandContextComplete {
  success: string;
  dry: string;
}

export interface CommandContext {
  dryRun?: boolean;
  fs: FileSystem;
  runCommand: CommandRunner;
  runCommandWithEnv(
    command: string,
    args: string[],
    options?: { cwd?: string; env?: Record<string, string | undefined> }
  ): Promise<CommandRunnerResult>;
  flushDryRun(options?: { emitIfEmpty?: boolean }): void;
  complete(messages: CommandContextComplete): void;
  finalize(): void;
}

export interface CommandContextFactoryInit {
  fs: FileSystem;
}

export interface CommandContextFactory {
  create(options: CommandContextOptions): CommandContext;
}

export function createCommandContextFactory(
  init: CommandContextFactoryInit
): CommandContextFactory {
  const { fs } = init;
  let feedbackEmitted = false;

  // The feedback footer closes the panel, so it must be emitted once per
  // process rather than once per sub-operation that finalizes.
  const emitFeedbackOnce = (logger: ScopedLogger): void => {
    if (feedbackEmitted) {
      return;
    }
    feedbackEmitted = true;
    logger.feedback("Problems?", FEEDBACK_URL);
  };

  const create = (options: CommandContextOptions): CommandContext => {
    if (!options.dryRun) {
      return {
        dryRun: false,
        fs,
        runCommand: options.runner,
        runCommandWithEnv(command, args, runOptions) {
          return options.runner(command, args, {
            cwd: runOptions?.cwd,
            env: runOptions?.env
          });
        },
        flushDryRun() {},
        complete(messages) {
          options.logger.success(messages.success);
        },
        finalize() {
          emitFeedbackOnce(options.logger);
        }
      };
    }

    const recorder = new DryRunRecorder();
    const proxyFs = createDryRunFileSystem(fs, recorder);
    const recordedCommands = new Set<string>();
    let hasEmittedChanges = false;

    const flush = (emitIfEmpty = false): void => {
      const operations = recorder.drain();
      // No-op operations must not suppress the summary: an agent that is already
      // configured still ensures directories and rewrites identical content.
      if (operations.some(describesChange)) {
        hasEmittedChanges = true;
      }
      if (operations.length === 0) {
        if (emitIfEmpty && !hasEmittedChanges) {
          const lines = formatDryRunOperations(operations);
          for (const line of lines) {
            options.logger.info(line);
          }
        }
        return;
      }

      for (const line of formatDryRunOperations(operations)) {
        const base = extractBaseCommand(line);
        if (!recordedCommands.has(base)) {
          options.logger.info(line);
          recordedCommands.add(base);
        }
      }
    };

    return {
      dryRun: true,
      fs: proxyFs,
      runCommand: options.runner,
      runCommandWithEnv(command, args, runOptions) {
        return options.runner(command, args, {
          cwd: runOptions?.cwd,
          env: runOptions?.env
        });
      },
      flushDryRun({ emitIfEmpty }: { emitIfEmpty?: boolean } = {}) {
        flush(Boolean(emitIfEmpty));
      },
      complete(messages) {
        options.logger.dryRun(messages.dry);
        flush(true);
      },
      finalize() {
        emitFeedbackOnce(options.logger);
      }
    };
  };

  return { create };
}

export function createLoggingCommandRunner(
  runner: CommandRunner,
  logger: ScopedLogger
): CommandRunner {
  return async (
    command,
    args,
    options
  ): Promise<CommandRunnerResult> => {
    const rendered = [command, ...args].join(" ").trim();
    const suffix = options?.cwd ? ` (cwd: ${options.cwd})` : "";
    logger.verbose(`> ${rendered}${suffix}`);
    if (options) {
      return runner(command, args, options);
    }
    return runner(command, args);
  };
}

function extractBaseCommand(message: string): string {
  const raw = stripAnsi(message);
  const detailIndex = raw.indexOf(" #");
  return detailIndex >= 0 ? raw.slice(0, detailIndex).trim() : raw.trim();
}

function stripAnsi(value: string): string {
  return value.replace(/\u001B\[[0-9;]*m/g, "");
}
